import React, { useEffect } from 'react';
import { Mic } from 'lucide-react';
import { useApp } from '@/contexts/AppContext';
import { speechRecognition } from '@/services/speechRecognition';
import { useToast } from '@/hooks/use-toast';

export const VoiceButton: React.FC<{ className?: string }> = ({ className }) => {
  const { isListening, setIsListening, setTranscript } = useApp();
  const { toast } = useToast();

  useEffect(() => {
    // Check browser compatibility first
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      console.error('Speech Recognition is not supported in this browser');
      toast({
        title: "Browser Not Supported",
        description: "Please use Chrome, Safari, or Firefox for voice input.",
        variant: "destructive"
      });
      return;
    }

    // Set up speech recognition listeners
    speechRecognition.setOnListeningChange((listening) => {
      console.log('Listening state changed:', listening);
      setIsListening(listening);
      if (!listening) {
        // If we stop listening without a transcript, show a message
        if (!speechRecognition.getTranscript()) {
          toast({
            title: "No speech detected",
            description: "Please try speaking again.",
            variant: "destructive"
          });
        }
      }
    });
    
    speechRecognition.setOnTranscriptChange((text) => {
      console.log('Transcript updated:', text);
      setTranscript(text);
    });
    
    return () => {
      if (isListening) {
        console.log('Cleaning up speech recognition...');
        speechRecognition.stop();
        setTranscript('');
      }
    };
  }, [setIsListening, setTranscript, isListening, toast]);

  const handleVoiceInput = async (e: React.MouseEvent | React.TouchEvent) => {
    console.log('Voice button clicked/touched');
    e.preventDefault();
    e.stopPropagation();
    
    try {
      if (!isListening) {
        console.log('Requesting microphone permission...');
        const hasPermission = await speechRecognition.requestMicrophonePermission();
        
        if (!hasPermission) {
          console.error('Microphone permission denied');
          toast({
            title: "Microphone Access Required",
            description: "Please allow microphone access in your browser settings.",
            variant: "destructive"
          });
          return;
        }
        
        setTranscript(''); // Clear any previous transcript
        console.log('Starting speech recognition...');
        const success = await speechRecognition.start();
        console.log('Speech recognition start result:', success);
        
        if (!success) {
          throw new Error('Failed to start speech recognition');
        }
      } else {
        console.log('Stopping speech recognition...');
        speechRecognition.stop();
        setIsListening(false);
      }
    } catch (error) {
      console.error('Error handling voice input:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "There was a problem with the voice input. Please try again.",
        variant: "destructive"
      });
      setIsListening(false);
      setTranscript('');
    }
  };

  return (
    <button
      onClick={handleVoiceInput}
      className={`flex h-28 w-28 items-center justify-center 
                 rounded-full shadow-xl transition-all duration-300
                 ${isListening 
                   ? 'bg-primary shadow-primary/50 scale-110' 
                   : 'bg-primary hover:scale-105'}
                 active:scale-95 touch-manipulation select-none
                 ${className || ''}`}
      aria-label={isListening ? 'Stop voice input' : 'Start voice input'}
    >
      <Mic className={`h-12 w-12 text-white ${isListening ? 'animate-pulse' : ''}`} />
    </button>
  );
};

export default VoiceButton; 